import React from 'react';
import PropTypes from 'prop-types';
import { TrophyIcon, ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';
import { useFont } from '../../contexts/FontContext';
import { getFrameUIProperties } from '../../utils/frameStyleUtils';

// Helper to properly quote font names for CSS
const quoteFontFamily = (font) => {
  if (!font) return '"Inter", sans-serif';
  return `"${font}", sans-serif`;
};

// Animated score that counts up when value changes (large/TV mode only)
const AnimatedScore = ({ value, className, style }) => {
  const [display, setDisplay] = React.useState(value);
  const prevRef = React.useRef(value);
  const rafRef = React.useRef(null);

  React.useEffect(() => {
    const from = prevRef.current;
    const to = typeof value === 'number' ? value : parseInt(value, 10) || 0;
    prevRef.current = to;
    if (from === to || isNaN(from)) { setDisplay(value); return; }

    const duration = 600;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setDisplay(Math.round(from + (to - from) * eased).toString());
      if (t < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [value]);

  return <span className={className} style={style}>{display}</span>;
};

const LeaderboardDisplay = ({
  entries,
  userEntry,
  theme = {},
  game,
  onJoinLeaderboard,
  hasJoined = false,
  showTitle = true,
  maxHeight = 'max-h-[160px]',
  large = false,
  blurUntilJoined = false,
  loading = false,
  highlightedEntryIds = new Set(),
  rankChanges = new Map(),
  newEntryIds = new Set(),
  showTableHead = true,
  naturalHeight = false,
  seamless = false,
}) => {
  const { loadFont } = useFont();
  
  // Use the standard 7-color system
  const customColors = game?.options?.customColors || {};
  
  const colors = {
    background: customColors.surface || '#FFFFFF',
    backgroundOpacity: 0.9,
    // Match the rest of the card body so "Top Scores" reads with the same
    // contrast as "Score" / "Made with..." instead of inheriting primary.
    leaderboardTitleText: customColors.text || '#1F2937',
    rankText: customColors.text || '#6B7280',
    nameText: customColors.text || '#111827',
    scoreText: customColors.primary || '#4F46E5',
    userAccent: customColors.primaryAction || '#F59E0B'
  };
  
  const typography = theme?.typography || {};
  const primaryFont = typography.primaryFont || 'Inter';
  const fontFamily = quoteFontFamily(primaryFont);
  
  const frameStyle = theme?.frameStyle ?? game?.options?.frameStyle ?? 'clean';
  const uiProps = getFrameUIProperties(frameStyle);
  
  // Responsive Tailwind font size classes (larger tiers for TV / big displays when large=true)
  const fontSize = {
    xs: 'text-sm md:text-base',
    sm: 'text-base md:text-lg',
    base: 'text-lg md:text-xl',
    lg: 'text-2xl md:text-3xl lg:text-4xl',
    xl: 'text-3xl md:text-4xl lg:text-5xl',
    tv: 'text-2xl md:text-3xl lg:text-4xl', // names on TV / large display
  };

  // Load primary font using centralized font loader
  React.useEffect(() => {
    loadFont(primaryFont);
  }, [primaryFont, loadFont]);

  const shouldBlur = blurUntilJoined && !hasJoined;

  // Subtle border only – defines the section without feeling heavy
  const getContainerStyles = () => ({
    borderRadius: uiProps.borderRadiusSmall,
    border: `1px solid ${colors.rankText}18`,
  });

  // Skeleton row for loading state
  const SkeletonRow = ({ rank }) => (
    <tr>
      <td className={`${large ? 'py-2' : 'py-1'} pl-3 pr-1 w-10`} style={{ width: '2.5rem' }}>
        <div className="flex items-center justify-center">
          <span
            className={`font-bold flex items-center justify-center ${large ? 'w-7 h-7' : 'w-5 h-5'} ${large ? fontSize.sm : fontSize.xs}`}
            style={{
              color: '#FFFFFF',
              backgroundColor: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32',
              fontFamily,
              borderRadius: uiProps.isPixelated ? '0' : '50%',
              lineHeight: 1,
              opacity: 0.5,
            }}
          >
            {rank}
          </span>
        </div>
      </td>
      <td className={`pl-1 pr-3 ${large ? 'py-2' : 'py-1'}`}>
        <div className={`rounded bg-current animate-pulse ${large ? 'h-5 w-24' : 'h-4 w-20'}`} style={{ color: `${colors.nameText}20` }} />
      </td>
      <td className={`pl-2 pr-3 text-right ${large ? 'py-2' : 'py-1'}`} style={{ width: large ? '6rem' : '5.5rem' }}>
        <div className={`rounded bg-current animate-pulse ml-auto ${large ? 'h-5 w-12' : 'h-4 w-10'}`} style={{ color: `${colors.scoreText}20` }} />
      </td>
    </tr>
  );

  return (
    <div
      className="relative"
      style={seamless ? undefined : getContainerStyles()}
    >
      {showTitle && (
        <h3
          className={`font-bold pt-2 pb-1 px-3 flex items-center ${fontSize.sm}`}
          style={{ color: colors.leaderboardTitleText, fontFamily }}
        >
          Top Scores
        </h3>
      )}

      <div className={naturalHeight ? 'pb-0' : `${maxHeight} overflow-y-auto overflow-x-hidden scrollbar-thin pb-0`}>
            <table className="min-w-full table-fixed">
              {large && showTableHead && (
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.rankText}30` }}>
                    <th className={`pl-3 pr-1 w-10 text-left font-semibold ${large ? 'py-2' : 'py-1.5'}`} style={{ width: '2.5rem', color: colors.rankText, fontFamily }}>#</th>
                    <th className={`pl-1 pr-3 min-w-0 text-left font-semibold ${large ? 'py-2 text-lg md:text-xl' : 'py-1.5'}`} style={{ color: colors.rankText, fontFamily }}>Name</th>
                    <th className={`pl-2 pr-3 text-right font-semibold ${large ? 'py-2 text-lg md:text-xl' : 'py-1.5'}`} style={{ width: large ? '6rem' : '5.5rem', color: colors.rankText, fontFamily }}>Score</th>
                  </tr>
                </thead>
              )}
              <tbody>
                {loading ? (
                  <>
                    <SkeletonRow rank={1} />
                    <SkeletonRow rank={2} />
                    <SkeletonRow rank={3} />
                  </>
                ) : entries.length > 0 ? (
                  entries.map((entry, index) => {
                    const isUserRow = entry.isUser;
                    const rank = entry.rank ?? (index + 1);
                    const isTopThree = rank <= 3;
                    const rankDelta = rankChanges.get(entry.id);
                    const isNewEntry = newEntryIds.has(entry.id);
                    const isHighlighted = highlightedEntryIds.has(entry.id);
                    // Colors for top 3 ranks
                    const getTopThreeColor = (r) => {
                      if (r === 1) return '#FFD700'; // Gold
                      if (r === 2) return '#C0C0C0'; // Silver
                      if (r === 3) return '#CD7F32'; // Bronze
                      return null;
                    };
                    const topThreeColor = getTopThreeColor(rank);

                    const RowTag = isNewEntry && large ? motion.tr : 'tr';
                    const rowMotionProps = isNewEntry && large ? {
                      initial: { opacity: 0, x: 60 },
                      animate: { opacity: 1, x: 0 },
                      transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
                    } : {};

                    return (
                    <RowTag
                      key={entry.id}
                      {...rowMotionProps}
                      onClick={shouldBlur ? onJoinLeaderboard : undefined}
                      className={shouldBlur ? 'cursor-pointer' : ''}
                      style={{
                        backgroundColor: isHighlighted
                          ? `${colors.userAccent}12`
                          : isUserRow
                          ? `${colors.userAccent}15`
                          : 'transparent',
                      }}
                    >
                      {/* Rank column - fixed width */}
                      <td className={`${large ? 'py-2' : 'py-1'} pl-3 pr-1 w-10`} style={{ width: '2.5rem' }}>
                        <div className="flex items-center justify-center">
                          {isTopThree ? (
                            <span 
                              className={`font-bold flex items-center justify-center ${large ? 'w-7 h-7' : 'w-5 h-5'} ${large ? fontSize.sm : fontSize.xs}`}
                              style={{ 
                                color: '#FFFFFF',
                                backgroundColor: topThreeColor,
                                fontFamily,
                                borderRadius: uiProps.isPixelated ? '0' : '50%',
                                lineHeight: 1,
                              }}
                            >
                              {rank}
                            </span>
                          ) : (
                            <span 
                              className={`font-medium ${large ? fontSize.sm : fontSize.xs}`}
                              style={{ 
                                color: isUserRow 
                                  ? colors.userAccent || '#F59E0B'
                                  : colors.rankText,
                                fontFamily,
                              }}
                            >
                              {rank}
                            </span>
                          )}
                        </div>
                      </td>
                      
                      {/* Name column - extra padding-right so last letter is never clipped */}
                      <td className={`pl-1 pr-3 ${large ? 'py-2' : 'py-1'} min-w-0`}>
                        <div 
                          className={`font-medium ${isUserRow ? 'font-bold' : ''} flex items-center gap-1 min-w-0 overflow-hidden ${large ? fontSize.tv : fontSize.sm}`}
                          style={{ 
                            color: isUserRow 
                              ? colors.userAccent || '#F59E0B'
                              : colors.nameText,
                            fontFamily,
                          }}
                        >
                          <span className="block min-w-0 truncate">{entry.name}</span>
                          {large && rankDelta && rankDelta > 0 && (
                            <span className="shrink-0 flex items-center text-green-500">
                              <ChevronUpIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                              <span className="text-xs lg:text-sm font-bold">{rankDelta}</span>
                            </span>
                          )}
                          {large && rankDelta && rankDelta < 0 && (
                            <span className="shrink-0 flex items-center text-red-400">
                              <ChevronDownIcon className="w-4 h-4 lg:w-5 lg:h-5" />
                              <span className="text-xs lg:text-sm font-bold">{Math.abs(rankDelta)}</span>
                            </span>
                          )}
                          {isUserRow && (
                            <span 
                              className="shrink-0 px-1 py-0.5 text-sm font-bold"
                              style={{
                                backgroundColor: colors.userAccent || '#F59E0B',
                                color: '#FFFFFF',
                                borderRadius: uiProps.borderRadiusTiny,
                              }}
                            >
                              YOU
                            </span>
                          )}
                        </div>
                      </td>
                      
                      {/* Score column - fixed width; right padding so blur isn't clipped (red row still goes to container edge) */}
                      <td className={`pl-2 pr-3 ${large ? 'py-2' : 'py-1'} text-right`} style={{ width: large ? '6rem' : '5.5rem' }}>
                        {large ? (
                          <AnimatedScore
                            value={entry.score}
                            className={`font-bold tabular-nums inline-block max-w-full ${fontSize.lg}`}
                            style={{ color: isUserRow ? colors.userAccent || '#F59E0B' : colors.scoreText, fontFamily }}
                          />
                        ) : (
                          <span
                            className={`font-bold tabular-nums inline-block max-w-full ${fontSize.sm} ${shouldBlur && !isUserRow ? 'blur-[3px] select-none px-1' : ''}`}
                            style={{ color: isUserRow ? colors.userAccent || '#F59E0B' : colors.scoreText, fontFamily }}
                          >
                            {entry.displayScore}
                          </span>
                        )}
                      </td>
                    </RowTag>
                    );
                  })
                ) : (
                  <tr>
                    <td className={`${large ? 'py-2' : 'py-1.5'} pl-3 pr-1 w-10`}>
                      <div className="flex items-center justify-center">
                        <span 
                          className={`font-bold flex items-center justify-center ${large ? 'w-7 h-7' : 'w-5 h-5'} ${large ? fontSize.sm : fontSize.xs}`}
                          style={{ 
                            color: '#FFFFFF',
                            backgroundColor: '#FFD700',
                            fontFamily,
                            borderRadius: uiProps.isPixelated ? '0' : '50%',
                            lineHeight: 1,
                          }}
                        >
                          1
                        </span>
                      </div>
                    </td>
                    <td className={`pl-1 pr-3 ${large ? 'py-2' : 'py-1.5'}`}>
                      <span 
                        className={`font-medium ${large ? fontSize.tv : fontSize.sm}`}
                        style={{ color: colors.nameText || '#111827', fontFamily }}
                      >
                        Be the first!
                      </span>
                    </td>
                    <td className={`pl-2 pr-3 text-right ${large ? 'py-2' : 'py-1.5'}`} style={{ width: large ? '6rem' : '5.5rem' }}>
                      <span 
                        className={`font-bold tabular-nums ${large ? fontSize.lg : fontSize.sm}`}
                        style={{ color: colors.scoreText || '#4F46E5', fontFamily }}
                      >
                        -
                      </span>
                    </td>
                  </tr>
                )}

                {/* User's position if not already visible in the list */}
                {userEntry && 
                  !entries.some(entry => entry.id === userEntry.id) &&
                  entries.length > 0 && (
                  <>
                    <tr>
                      <td colSpan="3" className="py-0.5">
                        <div 
                          className="border-t border-dashed mx-2" 
                          style={{ borderColor: `${colors.rankText || '#6B7280'}40` }} 
                        />
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: `${colors.userAccent}15` }}>
                      <td className={`${large ? 'py-2' : 'py-1'} pl-3 pr-1 w-10`}>
                        <div className="flex items-center justify-center">
                          <span 
                            className={`font-medium ${large ? fontSize.sm : fontSize.xs}`}
                            style={{ color: colors.userAccent || '#F59E0B', fontFamily }}
                          >
                            {userEntry.rank}
                          </span>
                        </div>
                      </td>
                      <td className={`pl-1 pr-3 ${large ? 'py-2' : 'py-1'}`}>
                        <div 
                          className={`font-bold flex items-center gap-1 min-w-0 overflow-hidden ${large ? fontSize.tv : fontSize.sm}`}
                          style={{ color: colors.userAccent || '#F59E0B', fontFamily }}
                        >
                          <span className="block min-w-0 truncate">{userEntry.name || 'You'}</span>
                          <span 
                            className="shrink-0 px-1 py-0.5 text-sm font-bold"
                            style={{
                              backgroundColor: colors.userAccent || '#F59E0B',
                              color: '#FFFFFF',
                              borderRadius: uiProps.borderRadiusTiny,
                            }}
                          >
                            YOU
                          </span>
                        </div>
                      </td>
                      <td className={`pl-2 pr-3 text-right ${large ? 'py-2' : 'py-1'}`} style={{ width: large ? '6rem' : '5.5rem' }}>
                        <span 
                          className={`font-bold tabular-nums ${large ? fontSize.lg : fontSize.sm}`}
                          style={{ color: colors.userAccent || '#F59E0B', fontFamily }}
                        >
                          {userEntry.displayScore}
                        </span>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
    </div>
  );
};

LeaderboardDisplay.propTypes = {
  entries: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    score: PropTypes.number.isRequired
  })).isRequired,
  userEntry: PropTypes.shape({
    name: PropTypes.string,
    score: PropTypes.number.isRequired,
    rank: PropTypes.number.isRequired
  }),
  theme: PropTypes.object,
  game: PropTypes.shape({
    gameType: PropTypes.string.isRequired,
    // ... other game props
  }),
  onJoinLeaderboard: PropTypes.func,
  hasJoined: PropTypes.bool,
  showTitle: PropTypes.bool,
  loading: PropTypes.bool,
  highlightedEntryIds: PropTypes.instanceOf(Set),
};

export default LeaderboardDisplay; 